import { useState, useEffect, useCallback } from 'react';
import { loadStateListByCountry, loadCityListByCountryState } from '../services/appServices';
import { sortCountryLov } from '../lovHelper';
import { getStateLabelByCountryName } from '../locationHelper';
import { PLACEHOLDER_FIELD_COUNTRY, PLACEHOLDER_FIELD_STATE, PLACEHOLDER_FIELD_CITY, getStateLovPlaceholder, getCityLovPlaceholder } from '../placeholderHelper';

import _get from 'lodash/get';
import _find from 'lodash/find';

const defaultGetFieldError = (/* name */) => {
    return false;
};

const useCountryStateCity = ({
    defaultCountry = null,
    defaultState = null,
    defaultCity = null,
    defaultCityLocation = null,
    defaultCountryCurrency = null,
    defaultCountryLov = null,
    defaultStateLov = null,
    defaultCityLov = null,
    setIsDirty,
    // invalidRequiredFields,
    // setInvalidRequiredFields,
    clearInvalidRequiredField,
    getFieldError = defaultGetFieldError,
} = {}) => {
    const [countryLov, setCountryLovState] = useState(sortCountryLov(defaultCountryLov ? defaultCountryLov : []));
    const [stateLov, setStateLov] = useState(defaultStateLov ? defaultStateLov : []);
    const [cityLov, setCityLov] = useState(defaultCityLov ? defaultCityLov : []);
    const [country, setCountry] = useState(defaultCountry ? defaultCountry : null);
    const [state, setState] = useState(defaultState ? defaultState : null);
    const [city, setCity] = useState(defaultCity ? defaultCity : null);
    const [newCity, setNewCity] = useState(null);
    const [cityLocation, setCityLocation] = useState(defaultCityLocation ? defaultCityLocation : []);
    const [countryCurrency, setCountryCurrency] = useState(defaultCountryCurrency ? defaultCountryCurrency : []);

    const setCountryLov = options => {
        setCountryLovState(sortCountryLov(options));
    };

    useEffect(() => {
        setCountryLovState(sortCountryLov(defaultCountryLov));
    }, [defaultCountryLov]);

    useEffect(() => {
        let isSubscribed = true;

        const loadNewStateCityLov = async () => {
            if (country) {
                console.log('>>>>>>>>> loadNewStateCityLov country', country);
                const { stateLov: newStateLov, cityLov: newCityLov } = await loadStateListByCountry(country, setStateLov, setCityLov);
                if (isSubscribed) {
                    setStateLov(newStateLov);
                    setCityLov(newCityLov);

                    // look up country currency
                    const currency = _get(
                        _find(countryLov, o => o.name === country),
                        'currency',
                    );
                    setCountryCurrency(currency);
                }
            } else {
                setCountryCurrency(null);
                setStateLov([]);
                setCityLov([]);
            }
        };
        loadNewStateCityLov();

        // in case the promise is resolved AFTER the component is unmounted.
        return () => {
            isSubscribed = false;
        };
    }, [country, countryLov]);

    useEffect(() => {
        let isSubscribed = true;

        const loadNewCityLov = async () => {
            if (country && state) {
                const { cityLov: newCityLov } = await loadCityListByCountryState(country, state);
                if (isSubscribed) {
                    setCityLov(newCityLov);
                }
            } else {
                setCityLov([]);
            }
        };
        loadNewCityLov();

        // in case the promise is resolved AFTER the component is unmounted.
        return () => {
            isSubscribed = false;
        };
    }, [country, state]);

    /**
     * The 'city' param can be in the following three forms:
     * 1. String
     * 2. Object { name, location }
     * 3. Object { name, label, inputValue }
     */
    const setCityData = useCallback(option => {
        const cityName = _get(option, 'name') === undefined ? option : option.name;
        const cityLocation = option?.location || null;

        setCity(cityName);
        setCityLocation(cityLocation);
        setNewCity(option?.inputValue);
    }, []);

    const handleChangeCountry = useCallback(
        (evt, option) => {
            setCountry(option?.name);
            setState(null);
            setCity(null);
            setCityLocation(null);
            setNewCity(undefined);
            setCountryCurrency(option?.currency);
            if (setIsDirty) setIsDirty(true);
            if (clearInvalidRequiredField) clearInvalidRequiredField('country');
        },
        [setCity, setIsDirty, clearInvalidRequiredField],
    );

    const handleChangeState = useCallback(
        (evt, option) => {
            setState(option?.name);
            setCity(null);
            setCityLocation(null);
            setNewCity(undefined);
            if (setIsDirty) setIsDirty(true);
            if (clearInvalidRequiredField) clearInvalidRequiredField('state');
        },
        [setCity, setIsDirty, clearInvalidRequiredField],
    );

    const handleChangeCity = useCallback(
        (evt, option) => {
            setCityData(option);
            if (setIsDirty) setIsDirty(true);
            if (clearInvalidRequiredField) clearInvalidRequiredField('city');
        },
        [setCityData, setIsDirty, clearInvalidRequiredField],
    );

    const setCountryStateCity = useCallback(
        ({ country = null, state = null, city = null, cityLocation = null }) => {
            setCountry(country);
            setState(state);
            setCity(city);
            setCityLocation(cityLocation);
            setNewCity(undefined);

            // look up country currency
            const currency = _get(
                _find(countryLov, o => o.name === country),
                'currency',
            );
            setCountryCurrency(currency);
        },
        [setCity, countryLov],
    );

    const stateDisabled = !country;
    const cityDisabled = !state || !country;
    const stateLabelValue = getStateLabelByCountryName(country);
    const statePlaceholder = stateDisabled ? '' : getStateLovPlaceholder(stateLov, PLACEHOLDER_FIELD_STATE);
    const cityPlaceholder = cityDisabled ? '' : getCityLovPlaceholder(cityLov, PLACEHOLDER_FIELD_CITY);

    return {
        countryLov,
        setCountryLov,
        stateLov,
        setStateLov,
        cityLov,
        setCityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        newCity,
        setNewCity,
        countryCurrency,
        setCountryCurrency,
        setCountryStateCity,
        countryProps: {
            options: countryLov,
            onChange: handleChangeCountry,
            value: country,
            placeholder: PLACEHOLDER_FIELD_COUNTRY,
            error: getFieldError('country'),
            id: 'country',
        },
        stateProps: {
            options: stateLov,
            onChange: handleChangeState,
            value: state,
            disabled: !country,
            placeholder: statePlaceholder,
            error: getFieldError('state'),
            id: 'state',
        },
        cityProps: {
            options: cityLov,
            onChange: handleChangeCity,
            value: city,
            disabled: !state,
            placeholder: cityPlaceholder,
            error: getFieldError('city'),
            id: 'city',
        },
        stateLabelProps: {
            value: stateLabelValue,
        },
    };
};

export default useCountryStateCity;
